import { CommonModule } from '@angular/common';
import { Component, computed } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DeleteConfirmationComponent } from '../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { environment } from 'src/environments/environment.dev';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { InputComponent } from '../../../@core/components/ui/input/input.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { USER_ROLE, USER_TYPE } from '../../../@core/models/enums';
import { IShareUser, ISubscriptionResponse, IUser, IWorkspace, SHARE_ACCESS, SHARE_FEATURE, WORKSPACE_COLOR, WORKSPACE_TYPE } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { BlurScreenService } from '../../../@core/services/blur-screen.service';
import { FeatureFlagService } from '../../../@core/services/feature-flag.service';
import { SubscriptionStateService } from '../../../@core/services/subscription-state.service';
import { ToastService } from '../../../@core/services/toast.service';
import { ShareToUsersComponent } from '../../../@shared/components/share-to-users/share-to-users.component';
import { TextCasePipe } from '../../../@shared/pipes/textCase.pipe';
import { OrganizationSetupFormService } from '../../../contract-management/services/organization-setup-from.service';
import { ShareContractService } from '../../../contract-management/services/share-contract.service';
import { UserService } from '../../../contract-management/services/user.service';
import { UI_FEATURE } from 'src/app/@core/models/feature-mapping';
import { Subscription } from 'rxjs';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';

interface IWorkspaceItem {
  label: string;
  userCount: number;
  workspace: IWorkspace;
}

@Component({
  selector: 'app-workspace',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogComponent,
    DeleteConfirmationComponent,
    ButtonComponent,
    ShareToUsersComponent,
    PopoverComponent,
    TextCasePipe,
    InputComponent
  ],
  standalone: true,
  templateUrl: './workspace.component.html',
  styleUrl: './workspace.component.scss'
})
export class WorkspaceComponent {
  currentUser!: IUser | null | undefined;
  workspaces: (IWorkspace & { sharedUsers: IShareUser[] })[] = [];
  addedToWorkspaceShareSubscription!: Subscription;
  workspaceItems: IWorkspaceItem[] = [];
  currentWorkspace: IWorkspace | null | undefined = null;
  selectedWorkspace: IWorkspace | null = null;
  searchText: string = '';
  // Create Workspace Dialog
  createWorkspaceDialogVisible: boolean = false;
  deleteConfirmDialogVisible: boolean = false;
  workspaceForm: FormGroup;
  workspaceColors = [
    { value: WORKSPACE_COLOR.ORANGE, label: 'Orange', color: '#FF6B35' },
    { value: WORKSPACE_COLOR.GREEN, label: 'Green', color: '#4CAF50' },
    { value: WORKSPACE_COLOR.YELLOW, label: 'Yellow', color: '#FFC107' },
    { value: WORKSPACE_COLOR.RED, label: 'Red', color: '#F44336' },
    { value: WORKSPACE_COLOR.PURPLE, label: 'Purple', color: '#9C27B0' },
    { value: WORKSPACE_COLOR.BLUE, label: 'Blue', color: '#2196F3' }
  ];
  activeStep: number = 0;
  filterAssigneeSearchValue: string = '';
  filteredUsers: IUser[] = [];
  selectedFilter: any = {
    assignees: []
  };
  users: IUser[] = [];
  shareDetails: { users: IShareUser[], anyone: SHARE_ACCESS } = { users: [], anyone: SHARE_ACCESS.RESTRICTED }
  workspacePermissions: Map<string, SHARE_ACCESS | SHARE_ACCESS[]> = new Map()
  WORKSPACE_TYPE = WORKSPACE_TYPE
  USER_ROLE = USER_ROLE
  nextLoading = false
  subscriptionDetails!: ISubscriptionResponse;
  ENV = environment;

  hasAnyInnDocFeatures = computed(() => this.featureFlags.checkFeature(UI_FEATURE.DOCUMENTS).allowed);

  // Helper method to check if user has specific role (handles both single value and array)
  private hasRole(role: SHARE_ACCESS | SHARE_ACCESS[] | undefined, checkRole: SHARE_ACCESS): boolean {
    if (!role) return false;
    if (Array.isArray(role)) {
      return role.includes(checkRole);
    }
    return role === checkRole;
  }

  // Helper method to check if role includes any of the specified roles
  private hasAnyRole(role: SHARE_ACCESS | SHARE_ACCESS[] | undefined, checkRoles: SHARE_ACCESS[]): boolean {
    if (!role) return false;
    if (Array.isArray(role)) {
      return checkRoles.some(checkRole => role.includes(checkRole));
    }
    return checkRoles.includes(role);
  }

  constructor(
    private authService: AuthenticationService,
    private organizationSetupFormService: OrganizationSetupFormService,
    private subscriptionState: SubscriptionStateService,
    private fb: FormBuilder,
    private toast: ToastService,
    private shareService: ShareContractService,
    private userService: UserService,
    private router: Router,
    private blurScreenService: BlurScreenService,
    private featureFlags: FeatureFlagService,
    private socketService: SocketService
  ) {
    this.authService.currentUser.subscribe(res => this.currentUser = res)
    this.organizationSetupFormService.currentWorkspace.subscribe(res => this.currentWorkspace = res && this.workspaces.find(ws => ws._id === res._id))

    // Initialize workspace form
    this.workspaceForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(1)]],
      description: [''],
      color: [WORKSPACE_COLOR.ORANGE]
    });
    this.addedToWorkspaceShareSubscription = this.socketService.on<{ workspace: string, sharedUsers: IShareUser[] }>(SOCKET_EVENTS.ADDED_TO_WORKSPACE_SHARE).subscribe(([data]) => {
      this.workspaces = this.workspaces.map(workspace => {
        if (workspace._id.toString() === data.workspace) {
          return { ...workspace, sharedUsers: data.sharedUsers };
        }
        return workspace;
      });
      this.buildWorkspaceItems();
    })

  }

  ngOnInit(): void {
    // Initialize workspace items with just the create option
    this.buildWorkspaceItems();
    this.getWorkspaces();
  }

  ngOnDestroy(): void {
    if (this.addedToWorkspaceShareSubscription) {
      this.addedToWorkspaceShareSubscription.unsubscribe();
    }
  }

  getWorkspaces() {
    this.organizationSetupFormService.getWorkspaces().subscribe({
      next: (res) => {
        if (res.success) {
          this.workspaces = res.data;
          const newWorkspace = this.organizationSetupFormService.initializeWorkspaceFromList(this.workspaces);
          // If a new workspace was set, update the user's current workspace
          if (newWorkspace && this.currentUser?._id) {
            this.userService.updateUser({ _id: this.currentUser._id, currentWorkspace: newWorkspace._id }).subscribe();
          }
          this.getPermissions();
        } else {
          this.workspaces = [];
        }
        // Always build workspace items, even if no workspaces exist
        this.buildWorkspaceItems();
      },
      error: (error) => {
        console.error('Error fetching workspaces:', error);
        this.workspaces = [];
        this.buildWorkspaceItems();
      }
    })
  }

  getPermissions() {
    this.workspacePermissions.clear();
    if (!this.currentUser?._id) {
      return;
    }
    this.workspaces.forEach(workspace => {
      if (workspace._id && workspace.sharedUsers) {
        const currentUserShare = workspace.sharedUsers.find(
          user => user.user === this.currentUser?._id || user.email === this.currentUser?.email
        );

        if (currentUserShare?.role) {
          this.workspacePermissions.set(workspace._id, currentUserShare.role);
        }
      }
    });
  }

  canEditWorkspace(workspaceId: string | undefined) {
    if (!workspaceId) return true;
    const userRole = this.workspacePermissions.get(workspaceId);
    if (userRole) { // if user's role is added in workspace, that should take more preference than org role.
      return this.hasAnyRole(userRole, [SHARE_ACCESS.ADMIN, SHARE_ACCESS.OWNER]);
    }
    return (this.currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser?.role) : false)
  }


  canCreateWorkspace() {
    return this.currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser?.role) : false;
  }

  canDeleteWorkspace() {
    const isOrgAdmin = this.currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser?.role) : false
    const workspaceRole = this.selectedWorkspace?._id ? this.workspacePermissions.get(this.selectedWorkspace._id) : undefined;
    const isWorkspaceOwner = workspaceRole && this.hasRole(workspaceRole, SHARE_ACCESS.OWNER)
    const isWorkspaceAdmin = workspaceRole && this.hasRole(workspaceRole, SHARE_ACCESS.ADMIN)
    const isRoleGivenInWorkspace = !!workspaceRole

    if (isRoleGivenInWorkspace) {
      if ((isOrgAdmin && isWorkspaceAdmin) || isWorkspaceOwner) return true;
      return false;
    }
    return isOrgAdmin
  }

  get workspaceForShareComponent() {
    return this.selectedWorkspace || { _id: '' } as IWorkspace
  }

  getShareDetails() {
    if (!this.selectedWorkspace?._id) {
      if (this.currentUser) {
        this.shareDetails = {
          users: [
            {
              user: this.currentUser as IUser,
              email: this.currentUser.email,
              name: this.currentUser.fullName ?? '',
              role: SHARE_ACCESS.OWNER,
              type: USER_TYPE.INTERNAL
            }
          ],
          anyone: SHARE_ACCESS.RESTRICTED
        }
      }
      return
    };
    this.shareService.getShareDetails(this.selectedWorkspace?._id, SHARE_FEATURE.WORKSPACE).subscribe(res => {
      if (res.success) {
        this.shareDetails = res.data ? res.data : { users: [], anyone: SHARE_ACCESS.RESTRICTED }
      }
    })
  }

  buildWorkspaceItems() {
    this.workspaceItems = this.workspaces.map(workspace => ({
      label: workspace.name,
      userCount: (workspace.sharedUsers ?? []).filter(user => user.type === USER_TYPE.INTERNAL).length,
      workspace: workspace,
    }));
  }

  selectWorkspace(workspace: IWorkspace) {
    if (!this.currentUser?._id || workspace._id === this.currentWorkspace?._id) return;
    this.userService.updateUser({ _id: this.currentUser?._id, currentWorkspace: workspace._id }).subscribe((res) => {
      if (res.success) {
        this.authService.setUserData(res.data)
        this.blurScreenService.enableBlur();
        setTimeout(() => {
          const currentUrl = this.router.url.split('?')[0].split('#')[0];
          const segments = currentUrl.split('/').filter(Boolean);
          if (segments.length > 0) {
            window.history.replaceState({}, '', `/${segments[0]}`);
            window.location.reload();

          } else {
            window.history.replaceState({}, '', '/');
            window.location.reload();
          }
        }, 1000)
      }
    })
  }

  openWorkspace(item: any, event: Event) {
    this.selectedWorkspace = item.workspace
    this.createWorkspaceDialogVisible = true
    this.workspaceForm.patchValue({
      name: item.workspace.name,
      description: item.workspace.description,
      color: item.workspace.color
    })
    this.getShareDetails()
  }

  openCreateWorkspaceDialog() {
    this.createWorkspaceDialogVisible = true;
    this.selectedWorkspace = null
    this.workspaceForm.reset({
      name: '',
      description: '',
      color: WORKSPACE_COLOR.ORANGE
    });
    this.getShareDetails()
  }

  selectColor(color: WORKSPACE_COLOR) {
    if (this.workspaceForm.get('color')?.value === color) {
      this.workspaceForm.patchValue({ color: '' });
    } else {
      this.workspaceForm.patchValue({ color });
    }
  }

  get dialogHeader() {
    return this.selectedWorkspace ? 'Edit Workspace' : 'Create Workspace'
  }

  nextStep() {
    if (this.activeStep === 0) {
      if (!this.workspaceForm.valid) {
        this.toast.error('Please fill in all required fields.');
        return;
      }
      this.activeStep = 1;
    }
    else {
      if (!this.canEditWorkspace(this.selectedWorkspace?._id)) {
        this.closeCreateWorkspaceDialog();
      }
      this.createUpdateWorkspace()
      const updatedWorkspace = this.workspaceItems.find(item => item.workspace?._id == this.selectedWorkspace?._id)
      if (updatedWorkspace) {
        updatedWorkspace.userCount = this.shareDetails.users.filter(user => user.type === USER_TYPE.INTERNAL).length
      }
    }
  }

  createUpdateWorkspace() {
    if (!this.selectedWorkspace) { // Create
      this.nextLoading = true
      this.organizationSetupFormService.createWorkspace(this.workspaceForm.value).subscribe(async res => {
        if (res.success) {
          this.saveShareDetails(res.data._id)
          this.toast.success('Workspace created successfully')
          this.getWorkspaces()
          this.selectWorkspace(res.data)
        }
        this.nextLoading = false
      })
    }
    else { // Edit
      if (!this.selectedWorkspace?._id) return;
      const formValue = this.workspaceForm.value;
      if (this.selectedWorkspace.name === formValue.name && this.selectedWorkspace.description === formValue.description && this.selectedWorkspace.color === formValue.color) {
        this.saveShareDetails(this.selectedWorkspace?._id)
      } else {
        this.nextLoading = true
        this.organizationSetupFormService.updateWorkspace(this.selectedWorkspace?._id, formValue).subscribe(res => {
          if (res.success) {
            this.saveShareDetails(res.data._id)
            this.toast.success('Workspace updated successfully')
            this.getWorkspaces()
          }
          this.nextLoading = false
        })
      }
    }
  }

  saveShareDetails(workspaceId: string) {
    if (!workspaceId) return;
    this.shareService.updateShare(workspaceId, this.shareDetails, SHARE_FEATURE.WORKSPACE).subscribe(res => {
      if (res.success) {
        this.toast.success('Share details updated successfully')
      }
      this.closeCreateWorkspaceDialog();
    })
  }

  saveDefaultWorkspace() {
    if (!this.selectedWorkspace?._id) return;
    const formValue = this.workspaceForm.value;
    if (this.selectedWorkspace.name === formValue.name && this.selectedWorkspace.description === formValue.description && this.selectedWorkspace.color === formValue.color) {
      this.closeCreateWorkspaceDialog();
    }
    else {
      this.organizationSetupFormService.updateWorkspace(this.selectedWorkspace?._id, formValue).subscribe(res => {
        if (res.success) {
          this.toast.success('Workspace updated successfully')
          this.selectedWorkspace = res.data
          this.closeCreateWorkspaceDialog();
          this.getWorkspaces()
        }
      })
    }
  }

  closeCreateWorkspaceDialog() {
    this.createWorkspaceDialogVisible = false;
    setTimeout(() => {
      this.resetCreateWorkspaceDialog();
    }, 1000);
  }

  resetCreateWorkspaceDialog() {
    this.nextLoading = false;
    this.activeStep = 0;

    this.workspaceForm.reset({
      name: '',
      description: '',
      color: WORKSPACE_COLOR.ORANGE
    });

    this.shareDetails = { users: [], anyone: SHARE_ACCESS.RESTRICTED };
    this.selectedWorkspace = null;
  }


  filterUsersInFilter() {
    if (!this.filterAssigneeSearchValue.trim()) {
      this.filteredUsers = [...this.users];
      return;
    }
    const query = this.filterAssigneeSearchValue.toLowerCase().trim();
    this.filteredUsers = this.users.filter((user: IUser) => {
      const fullName = (user.fullName || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });
  }

  deleteWorkspace() {
    if (!this.selectedWorkspace?._id) return;
    this.deleteConfirmDialogVisible = true;
  }

  confirmDeleteWorkspace() {
    if (!this.selectedWorkspace?._id) return;
    this.deleteConfirmDialogVisible = false;
    this.organizationSetupFormService.deleteWorkspace(this.selectedWorkspace._id).subscribe(res => {
      if (res.success) {
        this.toast.success('Workspace deleted successfully');
        this.closeCreateWorkspaceDialog();
        this.organizationSetupFormService.getWorkspaces().subscribe(workspacesRes => {
          if (workspacesRes.success && workspacesRes.data.length > 0) {
            this.selectWorkspace(workspacesRes.data[0]);
          } else {
            this.blurScreenService.enableBlur();
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        });
      }
    });
  }

  get shouldShowNextButton(): boolean {
    return !(this.activeStep === 1 && !this.canEditWorkspace(this.selectedWorkspace?._id));
  }

  get nextButtonLabel(): string {
    if (this.activeStep === 0) {
      return 'Next';
    }
    return 'Finish';
  }

  get filteredWorkspaces(): IWorkspaceItem[] {
    if (!this.searchText.trim()) {
      return this.workspaceItems;
    }
    const query = this.searchText.toLowerCase().trim();
    return this.workspaceItems.filter((item: IWorkspaceItem) => {
      const label = (item.label || '').toLowerCase();
      return label.includes(query);
    });
  }

  removeUserFromShare(shareUser: Partial<IShareUser>) {
    this.shareDetails.users = this.shareDetails.users.filter(user => user.email !== shareUser.email)
  }

}
